import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { apiUrl, fetchJson } from '../../apiBase';
import useMaarifInitLists from './useMaarifInitLists';
import useMaarifStudentPerformance from './useMaarifStudentPerformance';
import { bandChipSx, bandTone, normalizeSortOrder } from './assessmentUiUtils';

function _pickBandLabel(totalScore, bandsSorted) {
  const t = Number(totalScore);
  if (!Number.isFinite(t)) return null;

  for (const b of bandsSorted || []) {
    if (!b) continue;
    const mn = b.min_score === null || b.min_score === undefined ? null : Number(b.min_score);
    const mx = b.max_score === null || b.max_score === undefined ? null : Number(b.max_score);
    const geMin = mn === null ? true : t >= mn;
    const leMax = mx === null ? true : t <= mx;
    if (geMin && leMax) return b.label || null;
  }
  return null;
}

function _categoryFromLabel(label) {
  const s = String(label || '').toLocaleLowerCase('tr-TR');
  if (s.includes('geliştir')) return 'geliştirilmeli';
  if (s.includes('orta')) return 'orta';
  if (s.includes('iyi')) return 'iyi';
  return null;
}

function _fmtStudentName(st) {
  if (!st) return '';
  const parts = [st.name, st.surname].filter(Boolean);
  return parts.join(' ').trim();
}

function _computeScaleRows({ criteria, items }) {
  const crit = Array.isArray(criteria) ? criteria : [];
  const scoreByCriterionId = new Map();
  (items || []).forEach((it) => {
    if (!it || it.criterion_id == null) return;
    scoreByCriterionId.set(String(it.criterion_id), it.score);
  });

  const groups = new Map();
  for (const c of crit) {
    if (!c || c.id == null) continue;
    const title = c.section_title ? String(c.section_title) : '—';
    if (!groups.has(title)) groups.set(title, []);
    groups.get(title).push(c);
  }

  const rows = [];
  for (const [title, cs] of groups.entries()) {
    const sorted = (cs || []).slice().sort((a, b) => normalizeSortOrder(a?.sort_order) - normalizeSortOrder(b?.sort_order));

    let sum = 0;
    let max = 0;
    let scoredCount = 0;

    for (const c of sorted) {
      const raw = scoreByCriterionId.get(String(c.id));
      const n = raw === null || raw === undefined || raw === '' ? null : Number(raw);
      if (Number.isFinite(n)) {
        sum += n;
        scoredCount += 1;
      }

      const scoreVals = Array.isArray(c?.score_values) ? c.score_values : [0, 1, 2, 3];
      const vmax = Math.max(...scoreVals.map((v) => (Number.isFinite(Number(v)) ? Number(v) : 0)));
      max += Number.isFinite(vmax) ? vmax : 0;
    }

    rows.push({ title, sum, max, scoredCount, criterionCount: sorted.length });
  }

  rows.sort((a, b) => String(a.title).localeCompare(String(b.title), 'tr-TR'));
  return rows;
}

function SummaryBars({ counts }) {
  const total = Math.max(0, (counts?.geliştirilmeli || 0) + (counts?.orta || 0) + (counts?.iyi || 0));
  const mkRow = (label, value, tone) => {
    const pct = total > 0 ? (100 * value) / total : 0;
    return (
      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ minWidth: 140 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {value}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={tone}
            sx={{ height: 10, borderRadius: 10 }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {mkRow('Geliştirilmeli', counts?.geliştirilmeli || 0, 'warning')}
      {mkRow('Orta', counts?.orta || 0, 'info')}
      {mkRow('İyi', counts?.iyi || 0, 'success')}
    </Box>
  );
}

function computeBandCounts(tables) {
  const out = { geliştirilmeli: 0, orta: 0, iyi: 0 };
  const arr = Array.isArray(tables) ? tables : [];
  arr.forEach((t) => {
    const bandsSorted = (t?.bands || []).slice().sort((a, b) => normalizeSortOrder(a?.sort_order) - normalizeSortOrder(b?.sort_order));
    const label = t?.band_label || _pickBandLabel(t?.total_score, bandsSorted);
    const cat = _categoryFromLabel(label);
    if (!cat) return;
    out[cat] += 1;
  });
  return out;
}

export default function StudentMaarifPerformancePage({ onError, schoolId }) {
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const studentsReqRef = useRef(0);

  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  const notifyError = useCallback((msg) => {
    onErrorRef.current && onErrorRef.current(msg);
  }, []);

  const { classes, courses, loadingInit } = useMaarifInitLists({ schoolId, notifyError });

  // Default selections
  useEffect(() => {
    setSelectedClass((prev) => {
      if (!prev?.id) return classes[0] || null;
      const stillThere = (classes || []).find((c) => String(c?.id) === String(prev.id));
      return stillThere || (classes[0] || null);
    });
  }, [classes]);

  useEffect(() => {
    setSelectedCourse((prev) => {
      if (!prev?.id) return null;
      const stillThere = (courses || []).find((c) => String(c?.id) === String(prev.id));
      return stillThere || null;
    });
  }, [courses]);

  // Load students for selected class
  useEffect(() => {
    setStudents([]);
    setSelectedStudent(null);
    setLoadingStudents(false);

    if (!schoolId || !selectedClass?.id) return;
    const rid = ++studentsReqRef.current;

    const run = async () => {
      setLoadingStudents(true);
      try {
        const res = await fetchJson(
          apiUrl(`/admin/students/${encodeURIComponent(schoolId)}/by-class/${encodeURIComponent(selectedClass.id)}`)
        );
        if (rid !== studentsReqRef.current) return;
        if (!res.ok) throw new Error('students');
        const rows = await res.json();
        const mapped = (rows || [])
          .filter((s) => s && s.id != null)
          .map((s) => ({
            id: s.id,
            name: String(s.name || ''),
            surname: String(s.surname || ''),
          }))
          .sort((a, b) => _fmtStudentName(a).localeCompare(_fmtStudentName(b), 'tr-TR'));
        setStudents(mapped);
        setSelectedStudent(mapped[0] || null);
      } catch (e) {
        if (rid !== studentsReqRef.current) return;
        notifyError && notifyError('Öğrenciler yüklenemedi');
        setStudents([]);
        setSelectedStudent(null);
      } finally {
        if (rid !== studentsReqRef.current) return;
        setLoadingStudents(false);
      }
    };
    run();
  }, [schoolId, selectedClass?.id, notifyError]);

  const { loading, tables } = useMaarifStudentPerformance({
    schoolId,
    classId: selectedClass?.id,
    studentId: selectedStudent?.id,
    lessonId: selectedCourse?.id ?? null,
    enabled: !!schoolId && !!selectedClass?.id && !!selectedStudent?.id,
    notifyError,
  });

  const counts = useMemo(() => {
    return computeBandCounts(tables);
  }, [tables]);

  const pageLoading = loadingInit || loadingStudents || loading;

  const tablesSorted = useMemo(() => {
    const arr = Array.isArray(tables) ? tables : [];
    return arr.slice().sort((a, b) => {
      const lna = String(a?.lesson_name || '');
      const lnb = String(b?.lesson_name || '');
      const lc = lna.localeCompare(lnb, 'tr-TR');
      if (lc !== 0) return lc;

      const la = normalizeSortOrder(a?.learning_area_id);
      const lb = normalizeSortOrder(b?.learning_area_id);
      if (la !== lb) return la - lb;
      return String(a?.learning_activity_name || '').localeCompare(String(b?.learning_activity_name || ''), 'tr-TR');
    });
  }, [tables]);

  const lessons = useMemo(() => {
    const m = new Map();
    (tablesSorted || []).forEach((t) => {
      const lid = t?.lesson_id;
      if (lid == null) return;
      const key = String(lid);
      if (!m.has(key)) {
        m.set(key, {
          lesson_id: lid,
          lesson_name: String(t?.lesson_name || ''),
          tables: [],
        });
      }
      m.get(key).tables.push(t);
    });

    return Array.from(m.values()).sort((a, b) => String(a.lesson_name).localeCompare(String(b.lesson_name), 'tr-TR'));
  }, [tablesSorted]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Öğrenci Performansı (Yeni Maarif Model)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Şube ve öğrenci seçerek değerlendirme sonuçlarını görüntüleyin. Ders seçimi opsiyoneldir.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 240 }}
            options={classes || []}
            loading={loadingInit}
            value={selectedClass}
            onChange={(_, v) => setSelectedClass(v)}
            getOptionLabel={(o) => String(o?.name || '')}
            renderInput={(params) => <TextField {...params} label="Şube" />}
          />

          <Autocomplete
            size="small"
            sx={{ minWidth: 280 }}
            options={students || []}
            loading={loadingStudents}
            value={selectedStudent}
            onChange={(_, v) => setSelectedStudent(v)}
            getOptionLabel={(o) => _fmtStudentName(o)}
            renderInput={(params) => <TextField {...params} label="Öğrenci" />}
          />

          <Autocomplete
            size="small"
            sx={{ minWidth: 320 }}
            options={courses || []}
            loading={loadingInit}
            value={selectedCourse}
            onChange={(_, v) => setSelectedCourse(v)}
            getOptionLabel={(o) => String(o?.name || '')}
            renderInput={(params) => <TextField {...params} label="Ders (opsiyonel)" />}
          />

          <Chip
            label={pageLoading ? 'Yükleniyor…' : `Değerlendirme: ${tablesSorted.length}`}
            variant="outlined"
            sx={{ height: 32 }}
          />
        </Box>

        {pageLoading ? <LinearProgress sx={{ mt: 2 }} /> : null}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
          Seviye Dağılımı
        </Typography>
        <SummaryBars counts={counts} />
      </Paper>

      {(lessons || []).map((lesson) => {
        const perLessonCounts = computeBandCounts(lesson.tables);

        return (
          <Accordion
            key={`lesson:${lesson.lesson_id}`}
            defaultExpanded={(lessons || []).length === 1}
            disableGutters
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', width: '100%' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                    {lesson.lesson_name || 'Ders'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Etkinlik sonucu: {(lesson.tables || []).length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Chip size="small" label={`Geliştirilmeli: ${perLessonCounts.geliştirilmeli || 0}`} />
                  <Chip size="small" label={`Orta: ${perLessonCounts.orta || 0}`} />
                  <Chip size="small" label={`İyi: ${perLessonCounts.iyi || 0}`} />
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Ders Bazlı Seviye Dağılımı
                </Typography>
                <SummaryBars counts={perLessonCounts} />
              </Paper>

              {(lesson.tables || []).map((t) => {
                const bandsSorted = (t?.bands || []).slice().sort((a, b) => normalizeSortOrder(a?.sort_order) - normalizeSortOrder(b?.sort_order));
                const label = t?.band_label || _pickBandLabel(t?.total_score, bandsSorted);
                const tone = bandTone(label);
                const scaleRows = _computeScaleRows({ criteria: t?.criteria, items: t?.items });

                return (
                  <Accordion
                    key={`${t.session_id || 'no-session'}:${t.assessment_id}`}
                    disableGutters
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                      mb: 1.25,
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', width: '100%' }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            {t.learning_activity_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t.learning_area_name} • {t.assessment_name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {t.assessed_on ? <Chip size="small" variant="outlined" label={String(t.assessed_on)} /> : null}
                          {label ? <Chip size="small" label={String(label)} sx={bandChipSx(tone)} /> : null}
                          {Number.isFinite(Number(t?.total_score)) ? <Chip size="small" variant="outlined" label={`Toplam: ${Number(t.total_score)}`} /> : null}
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                        Ölçek Sonuçları
                      </Typography>

                      <Table size="small" sx={{ mb: 1 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800 }}>Ölçek</TableCell>
                            <TableCell sx={{ fontWeight: 800 }} align="right">Toplam</TableCell>
                            <TableCell sx={{ fontWeight: 800 }} align="right">Maks</TableCell>
                            <TableCell sx={{ fontWeight: 800 }} align="right">Kriter</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {scaleRows.map((r) => (
                            <TableRow key={r.title}>
                              <TableCell>{r.title}</TableCell>
                              <TableCell align="right">{r.sum}</TableCell>
                              <TableCell align="right">{r.max}</TableCell>
                              <TableCell align="right">{r.scoredCount}/{r.criterionCount}</TableCell>
                            </TableRow>
                          ))}
                          {scaleRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography variant="body2" color="text.secondary">
                                  Ölçek bilgisi bulunamadı.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {!pageLoading && tablesSorted.length === 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Seçilen filtreler için gösterilecek değerlendirme sonucu bulunamadı.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
